import { useEffect, useRef } from 'react';

interface SEOMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

const baseUrl = 'https://www.mangovideo.com';

export default function SEOMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schema,
}: SEOMetaProps) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) setMeta('og:url', `${baseUrl}${canonicalUrl}`, true);
    setMeta('og:locale', 'zh_CN', true);
    setMeta('og:site_name', '芒果视频', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalUrl) {
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${baseUrl}${canonicalUrl}`);
    } else if (link) {
      link.remove();
    }

    if (schema && !scriptRef.current) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptRef.current = script;
    }

    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schema]);

  return null;
}

export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '芒果视频',
    url: 'https://www.mangovideo.com',
    description: '芒果视频免费在线观看电影电视剧全集，替代西瓜视频免费、91视频等平台。无需付费即可享受海量影视资源，免费播放不收费，高清流畅不缓冲。',
    logo: 'https://www.mangovideo.com/logo.png',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      email: 'contact@mangovideo.com',
    },
    sameAs: [
      'https://weibo.com/mangovideo',
      'https://www.douyin.com/user/mangovideo',
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
      streetAddress: '望京科技园A座18层',
    },
  };
}

export function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '芒果视频',
    url: 'https://www.mangovideo.com',
    description: '芒果视频免费在线观看电影电视剧全集，无需付费即可享受海量影视资源。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.mangovideo.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

export function getFAQPageSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function getArticleSchema(article: {
  title: string;
  description: string;
  image: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  url: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished || '2024-01-01',
    dateModified: article.dateModified || '2024-08-01',
    author: {
      '@type': 'Organization',
      name: article.author || '芒果视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '芒果视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.mangovideo.com${article.url}`,
    },
  };
}

export function getBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://www.mangovideo.com${item.url}`,
    })),
  };
}